import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { IMAGE_BASE_URL } from '../config';

const CarDetail = () => {
  const { slug } = useParams(); // Změna z id na slug
  const [car, setCar] = useState(null);
  const [carPhotos, setCarPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage();

  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        setLoading(true);
        const headers = { 'accept-language': language };
        const carData = await get(`/cars/${slug}`, headers); // Změna na slug
        setCar(carData);
        
        try {
          const photosData = await get(`/car-photos/${carData.id}`, headers); // Používáme ID pro fotky
          setCarPhotos(photosData || []);
        } catch (photoError) {
          console.error('[CarDetail] Error fetching car photos:', photoError);
          setCarPhotos([]);
        }
      } catch (error) {
        console.error('[CarDetail] Error fetching car details:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst detaily auta' : 'Failed to load car details');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCarDetails();
    }
  }, [slug, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Detail vozidla' : 'Car Details'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !car) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Detail vozidla' : 'Car Details'} />
        <div className="text-white text-center py-20">
          {error || (language === 'cs' ? 'Vozidlo nenalezeno' : 'Car not found')}
        </div>
        <Footer />
      </div>
    );
  }

  const carData = car.translations?.[language] || car.translations?.en || { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' };
  const specLabels = {
    spec_1: car.spec_icons?.spec_1_label?.[language] || (language === 'cs' ? 'Výkon' : 'Power'),
    spec_2: car.spec_icons?.spec_2_label?.[language] || (language === 'cs' ? 'Motor' : 'Engine'),
    spec_3: car.spec_icons?.spec_3_label?.[language] || (language === 'cs' ? 'Jezdec' : 'Driver'),
    spec_4: car.spec_icons?.spec_4_label?.[language] || (language === 'cs' ? 'Sezóna' : 'Season'),
  };
  const specIcons = {
    spec_1: car.spec_icons?.spec_1_icon || 'fa-tachometer-alt',
    spec_2: car.spec_icons?.spec_2_icon || 'fa-cog',
    spec_3: car.spec_icons?.spec_3_icon || 'fa-user',
    spec_4: car.spec_icons?.spec_4_icon || 'fa-calendar',
  };

  const getSpecValue = (spec) => {
    return carData[spec] || (spec === 'specs_1' && car.horsepower ? `${car.horsepower} ${language === 'cs' ? 'koní' : 'hp'}` : (language === 'cs' ? 'Není uvedeno' : 'Not specified'));
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={carData.name || (language === 'cs' ? 'Detail vozidla' : 'Car Details')} />

      <section className="py-16 bg-dark-gray relative" id={`car-${car.id}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-red-600/5 blur-3xl rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-red-600/5 blur-3xl rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="lg:w-1/2">
              <div className="relative overflow-hidden rounded-lg shadow-xl h-96">
                <img
                  src={car.image_path ? `${IMAGE_BASE_URL}${car.image_path}` : '/images/cars/placeholder.png'}
                  alt={carData.name || 'Car'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/cars/placeholder.png';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                
                {car.is_current && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {language === 'cs' ? 'Aktuální sezóna' : 'Current season'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:w-1/2">
              <h2 className="text-3xl font-title font-bold text-white mb-4">
                {carData.name || (language === 'cs' ? 'Bezejmenné auto' : 'Unnamed Car')}
              </h2>
              
              <div className="gradient-line mb-6"></div>
              
              {carData.description && (
                <div className="mb-6">
                  <h3 className="text-xl font-title font-bold text-white mb-2">
                    {language === 'cs' ? 'Popis' : 'Description'}
                  </h3>
                  <p className="text-gray-300">{carData.description}</p>
                </div>
              )}
              
              <div className="mb-8">
                <h3 className="text-xl font-title font-bold text-white mb-3">
                  {language === 'cs' ? 'Technické specifikace' : 'Technical Specifications'}
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <li className="flex items-start bg-black/30 p-4 rounded-lg">
                    <i className={`fas ${specIcons.spec_1} text-red-500 mr-3 mt-1`}></i>
                    <div>
                      <h4 className="text-white font-semibold">{specLabels.spec_1}</h4>
                      <p className="text-gray-300">{getSpecValue('specs_1')}</p>
                    </div>
                  </li>
                  <li className="flex items-start bg-black/30 p-4 rounded-lg">
                    <i className={`fas ${specIcons.spec_2} text-red-500 mr-3 mt-1`}></i>
                    <div>
                      <h4 className="text-white font-semibold">{specLabels.spec_2}</h4>
                      <p className="text-gray-300">{getSpecValue('specs_2')}</p>
                    </div>
                  </li>
                  <li className="flex items-start bg-black/30 p-4 rounded-lg">
                    <i className={`fas ${specIcons.spec_3} text-red-500 mr-3 mt-대학1`}></i>
                    <div>
                      <h4 className="text-white font-semibold">{specLabels.spec_3}</h4>
                      <p className="text-gray-300">{getSpecValue('specs_3')}</p>
                    </div>
                  </li>
                  <li className="flex items-start bg-black/30 p-4 rounded-lg">
                    <i className={`fas ${specIcons.spec_4} text-red-500 mr-3 mt-1`}></i>
                    <div>
                      <h4 className="text-white font-semibold">{specLabels.spec_4}</h4>
                      <p className="text-gray-300">{getSpecValue('specs_4')}</p>
                    </div>
                  </li>
                </ul>
              </div>
              
              <Link to="/cars-gallery" className="inline-flex items-center text-white hover:text-red-500 transition-colors">
                <i className="fas fa-arrow-left mr-2"></i>
                {language === 'cs' ? 'Zpět na galerii vozidel' : 'Back to Cars Gallery'}
              </Link>
            </div>
          </div>
          
          <div className="mt-16">
            <h3 className="text-2xl font-title font-bold text-white mb-6">
              {language === 'cs' ? 'Galerie vozidla' : 'Car Gallery'}
            </h3>
            
            {carPhotos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {carPhotos.map((photo, index) => {
                  const photoDesc = photo.translations?.[language]?.description || 
                                   photo.translations?.en?.description || '';
                  
                  return (
                    <div key={photo.id} className="overflow-hidden rounded-lg shadow-lg group">
                      <div className="relative h-64">
                        <img
                          src={photo.image_path ? `${IMAGE_BASE_URL}${photo.image_path}` : '/images/cars/placeholder.png'}
                          alt={`${carData.name || 'Car'} ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/cars/placeholder.png';
                          }}
                        />
                        {photoDesc && (
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-4">
                            <p className="text-white text-sm">{photoDesc}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 bg-black/20 rounded-lg">
                <i className="fas fa-camera text-gray-500 text-4xl mb-4"></i>
                <p className="text-gray-400">
                  {language === 'cs' ? 'Zatím nejsou k dispozici žádné fotografie' : 'No photos available yet'}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CarDetail;